import { Temporal } from "@js-temporal/polyfill";

/**
 * Portal's original year-month value, modeled on JS `Date`: `month` is
 * ZERO-INDEXED (January = 0, December = 11) — unlike `Temporal.PlainYearMonth`,
 * which is 1-indexed (January = 1). The two look identical (`{ year, month }`)
 * but disagree on what `month` means; TypeScript cannot catch a mix-up because
 * both sides are just numbers. Use `legacyYearMonthToTemporal` /
 * `legacyYearMonthFromTemporal` at any boundary with Temporal-based code
 * instead of constructing/reading `.month` directly across the two types.
 *
 * This is intentionally still the zero-indexed representation, not a full
 * migration to Temporal — see the dedicated planning date-model change for that.
 */
export type LegacyYearMonth = { year: number; month: number };

export const legacyYearMonthToTemporal = (
	value: LegacyYearMonth,
): Temporal.PlainYearMonth =>
	Temporal.PlainYearMonth.from({ year: value.year, month: value.month + 1 });

export const legacyYearMonthFromTemporal = (
	value: Temporal.PlainYearMonth,
): LegacyYearMonth => ({ year: value.year, month: value.month - 1 });

export const isLowerThanOrEqualTo = (
	a: LegacyYearMonth,
	b: LegacyYearMonth,
) => {
	return a.year < b.year || (a.year === b.year && a.month <= b.month);
};

export const isLargerThanOrEqualTo = (
	a: LegacyYearMonth,
	b: LegacyYearMonth,
) => {
	return a.year > b.year || (a.year === b.year && a.month >= b.month);
};

export const isYearMonthLowerThan = (
	a: LegacyYearMonth,
	b: LegacyYearMonth,
) => {
	return a.year < b.year || (a.year === b.year && a.month < b.month);
};

// Check if a is larger than b
export const isYearMonthLargerThan = (
	a: LegacyYearMonth,
	b: LegacyYearMonth,
) => {
	return a.year > b.year || (a.year === b.year && a.month > b.month);
};

export const dateToYearMonth = (date: Date): LegacyYearMonth => {
	return { year: date.getFullYear(), month: date.getMonth() };
};

export const dateFromYearMonth = (yearMonth: LegacyYearMonth) => {
	return new Date(yearMonth.year, yearMonth.month);
};

export const plainDateFromYearMonth = (yearMonth: LegacyYearMonth) => {
	return Temporal.PlainDate.from({
		year: yearMonth.year,
		month: yearMonth.month + 1,
		day: 1,
	});
};

export const isBetweenYearMonth = (
	value: LegacyYearMonth,
	min: LegacyYearMonth,
	max: LegacyYearMonth,
) => {
	return isLargerThanOrEqualTo(value, min) && isLowerThanOrEqualTo(value, max);
};

export const isDateBetweenYearMonth = (
	date: Date,
	min: LegacyYearMonth,
	max: LegacyYearMonth,
) => {
	const { year, month } = dateToYearMonth(date);
	const isHigherThanOrEqualToMin =
		year > min.year || (year === min.year && month >= min.month);
	const isLowerThanOrEqualToMax =
		year < max.year || (year === max.year && month <= max.month);
	return isHigherThanOrEqualToMin && isLowerThanOrEqualToMax;
};

export const differenceInMonthsYearMonth = (
	a: LegacyYearMonth,
	b: LegacyYearMonth,
) => {
	if (a.year === b.year) {
		return b.month - a.month;
	}
	let diff = (b.year - a.year) * 12;
	diff -= a.month;
	diff += b.month;
	return diff;
};
